export type Market = 'se' | 'intl';

export type LocationMode = 'postal_code' | 'manual_climate';

export type ClimateSnowLoadClass = 'low' | 'medium' | 'high' | 'severe';

export type ClimateWindExposure = 'sheltered' | 'normal' | 'exposed';

export type ClimateElevationBand = 'lowland' | 'upland' | 'alpine';

export interface ClimateProfile {
  snowLoadClass: ClimateSnowLoadClass;
  snowZone: number;
  windExposure?: ClimateWindExposure;
  elevationBand?: ClimateElevationBand;
}

export interface ClimateProfileOption {
  snowLoadClass: ClimateSnowLoadClass;
  snowZone: number;
  label: string;
  description: string;
}

export const CLIMATE_PROFILE_OPTIONS: ClimateProfileOption[] = [
  {
    snowLoadClass: 'low',
    snowZone: 1.5,
    label: 'Low snow load',
    description: 'Mild winters or coastal conditions with limited snow accumulation.',
  },
  {
    snowLoadClass: 'medium',
    snowZone: 2.0,
    label: 'Standard snow load',
    description: 'Typical winter snow for most temperate inland sites.',
  },
  {
    snowLoadClass: 'high',
    snowZone: 3.5,
    label: 'High snow load',
    description: 'Heavy snow conditions that need larger structural safety margins.',
  },
  {
    snowLoadClass: 'severe',
    snowZone: 4.5,
    label: 'Severe / alpine snow load',
    description: 'Mountain or extreme winter conditions. Local structural review is strongly advised.',
  },
];

export const DEFAULT_INTL_CLIMATE_PROFILE: ClimateProfile = {
  snowLoadClass: 'medium',
  snowZone: 2.0,
  windExposure: 'normal',
};

export function getClimateProfileOption(
  snowLoadClass: ClimateSnowLoadClass,
): ClimateProfileOption {
  return CLIMATE_PROFILE_OPTIONS.find((option) => option.snowLoadClass === snowLoadClass)
    ?? CLIMATE_PROFILE_OPTIONS[1];
}

export function normalizeClimateProfile(
  input?: Partial<ClimateProfile> | null,
): ClimateProfile {
  if (!input?.snowLoadClass) {
    return DEFAULT_INTL_CLIMATE_PROFILE;
  }

  const option = getClimateProfileOption(input.snowLoadClass);

  return {
    snowLoadClass: option.snowLoadClass,
    snowZone: typeof input.snowZone === 'number' ? input.snowZone : option.snowZone,
    windExposure: input.windExposure ?? 'normal',
    ...(input.elevationBand ? { elevationBand: input.elevationBand } : {}),
  };
}
